import React, { useEffect, useRef, useState } from 'react';
import { Calendar, Heart, Maximize2, X } from 'lucide-react';

export interface FeedCardProps {
  imageUrl: string | null;
  status: 'COMPLETED' | 'PROCESSING' | 'FAILED' | string;
  isFavorite?: boolean | null;
  productName: string;
  price?: string | null;
  retailer: string;
  date: string;
  category?: string | null;
  onOpenSession: () => void;
  onToggleFavorite: () => void;
  onLongPress: () => void;
}

const LONG_PRESS_MS = 450;

const FeedCard: React.FC<FeedCardProps> = ({
  imageUrl,
  status,
  isFavorite,
  productName,
  price,
  retailer,
  date,
  category,
  onOpenSession,
  onToggleFavorite,
  onLongPress,
}) => {
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef<number | null>(null);
  const longPressedRef = useRef(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const fsRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const startPress = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    longPressedRef.current = false;
    timerRef.current = window.setTimeout(() => {
      longPressedRef.current = true;
      onLongPress();
    }, LONG_PRESS_MS);
  };

  const clearPress = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => () => clearPress(), []);

  useEffect(() => {
    const onFsChange = () => {
      const anyDoc = document as any;
      const el = (document.fullscreenElement || anyDoc.webkitFullscreenElement) as Element | null;
      const mine = fsRef.current;
      setIsFullscreen(Boolean(el && mine && (el === mine || (el.contains && el.contains(mine)))));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange' as any, onFsChange as any);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange' as any, onFsChange as any);
    };
  }, []);

  const statusClass =
    String(status).toUpperCase() === 'COMPLETED'
      ? 'text-green-700 bg-green-100'
      : String(status).toUpperCase() === 'PROCESSING'
      ? 'text-yellow-700 bg-yellow-100'
      : 'text-red-700 bg-red-100';

  const statusText =
    String(status).toUpperCase() === 'COMPLETED'
      ? 'Voltooid'
      : String(status).toUpperCase() === 'PROCESSING'
      ? 'Bezig'
      : 'Mislukt';

  return (
    <div
      className="w-full mx-auto rounded-2xl overflow-hidden bg-black relative"
      onClick={() => { if (!longPressedRef.current) onOpenSession(); longPressedRef.current = false; }}
      onPointerDown={startPress}
      onPointerUp={clearPress}
      onPointerLeave={clearPress}
      onContextMenu={(e) => {
        e.preventDefault();
        longPressedRef.current = true;
        onLongPress();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpenSession();
      }}
    >
      <div ref={fsRef} className="relative w-full h-[100svh] sm:h-auto pt-0 sm:pt-[56.25%]">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={productName}
            loading="lazy"
            referrerPolicy="no-referrer"
            onLoad={() => setLoaded(true)}
            className="absolute inset-0 w-full h-full object-contain select-none"
            ref={imgRef}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-200" />
        )}
        {!loaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}

        <div className={`absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusClass}`}>
          {statusText}
        </div>

        <div className="absolute top-3 right-3 flex flex-col gap-2">
          {isFullscreen && (
            <button
              type="button"
              aria-label="Close fullscreen"
              onClick={(e) => {
                e.stopPropagation();
                if (document.fullscreenElement) {
                  const anyDoc = document as any;
                  (document.exitFullscreen?.() || anyDoc.webkitExitFullscreen?.())?.catch?.(() => {});
                }
              }}
              className="p-2 bg-black/60 text-white rounded-full shadow hover:bg-black/70"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            aria-pressed={!!isFavorite}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            className="p-2 bg-white/90 rounded-full shadow hover:bg-white"
          >
            <Heart className={`h-5 w-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
          </button>
          <button
            type="button"
            aria-label="Fullscreen"
            onClick={(e) => {
              e.stopPropagation();
              const el = fsRef.current || imgRef.current;
              if (el && (el.requestFullscreen || (el as any).webkitRequestFullscreen)) {
                try {
                  (el.requestFullscreen ? el.requestFullscreen() : (el as any).webkitRequestFullscreen());
                } catch {
                  if (imageUrl) window.open(imageUrl, '_blank', 'noopener,noreferrer');
                }
              } else if (imageUrl) {
                window.open(imageUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            className="p-2 bg-white/90 rounded-full shadow hover:bg-white"
          >
            <Maximize2 className="h-5 w-5 text-gray-700" />
          </button>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 pt-12 p-4"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.65) 100%)',
          }}
        >
          <div className="text-white">
            <div className="text-lg font-semibold leading-snug line-clamp-2">{productName}</div>
            {price ? <div className="text-base font-medium opacity-95">{price}</div> : null}
            <div className="text-sm opacity-90">{retailer}</div>
            <div className="mt-2 flex items-center gap-3 text-xs opacity-90">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {date}
              </span>
              {category ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/25 text-white">
                  {category}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedCard;
